<template>
  <el-dialog
    title="修改状态"
    :visible.sync="dialogVisible"
    width="600px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
    @open="handleOpen"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="100px">
      <el-form-item label="当前状态">
        <el-tag :type="getStatusTag(current_status)">
          {{ getStatusText(current_status) }}
        </el-tag>
      </el-form-item>
      
      <el-form-item label="目标状态" prop="status">
        <el-radio-group v-model="form.status" @change="handleStatusChange">
          <el-radio :label="0">识别中</el-radio>
          <el-radio :label="1">待归档</el-radio>
          <el-radio :label="2">已归档</el-radio>
        </el-radio-group>
      </el-form-item>
      
      <!-- 当选择待归档时显示识别时间 -->
      <el-form-item 
        v-if="form.status === 1" 
        label="识别时间" 
        prop="identify_time"
      >
        <el-date-picker
          v-model="form.identify_time"
          type="datetime"
          placeholder="选择识别时间"
          value-format="yyyy-MM-dd HH:mm:ss"
          format="yyyy-MM-dd HH:mm:ss"
          style="width: 100%;"
          :picker-options="datePickerOptions"
          :clearable="false"
        ></el-date-picker>
        <div class="form-tips">选择风险识别的具体时间</div>
      </el-form-item>
      
      <!-- 当选择已归档时显示签字 -->
      <el-form-item 
        v-if="form.status === 2" 
        label="签字确认" 
        prop="signature"
      >
        <el-input
          v-model="form.signature"
          placeholder="请输入签字内容（如：张 确认归档）"
          type="textarea"
          :rows="3"
          :maxlength="100"
          show-word-limit
        ></el-input>
        <div class="form-tips">请输入负责人的签字确认信息</div>
      </el-form-item>
      
      <!-- 状态变更说明 -->
      <el-form-item v-if="form.status !== current_status" label="变更说明">
        <div class="status-change-info">
          <i class="el-icon-info"></i>
          将从 <el-tag :type="getStatusTag(current_status)" size="small">{{ getStatusText(current_status) }}</el-tag>
          变更为 <el-tag :type="getStatusTag(form.status)" size="small">{{ getStatusText(form.status) }}</el-tag>
        </div>
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose" :disabled="loading">取消</el-button>
      <el-button 
        type="primary" 
        @click="handleSubmit" 
        :loading="loading"
        :disabled="form.status === current_status"
      >
        确定变更
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import Risk_controlApi from '@/api/risk_control.js';

export default {
  name: 'StatusEdit',
  props: {
    open_add: {
      type: Boolean,
      default: false
    },
    risk_control_id: {
      type: [String, Number],
      required: true
    },
    current_status: {
      type: Number,
      default: 0,
      validator: value => [0, 1, 2].includes(value)
    }
  },
  data() {
    const validateIdentifyTime = (rule, value, callback) => {
      if (this.form.status === 1 && !value) {
        callback(new Error('请选择识别时间'));
      } else {
        callback();
      }
    };
    
    const validateSignature = (rule, value, callback) => {
      if (this.form.status === 2 && !value) {
        callback(new Error('请输入签字内容'));
      } else if (this.form.status === 2 && value && (value.length < 2 || value.length > 100)) {
        callback(new Error('签字内容长度应在2-100个字符之间'));
      } else {
        callback();
      }
    };
    
    return {
      dialogVisible: false,
      loading: false,
      form: {
        status: 0,
        identify_time: '',
        signature: ''
      },
      rules: {
        status: [
          { required: true, message: '请选择目标状态', trigger: 'change' }
        ],
        identify_time: [
          { validator: validateIdentifyTime, trigger: 'change' }
        ],
        signature: [
          { validator: validateSignature, trigger: 'blur' }
        ]
      },
      datePickerOptions: {
        disabledDate(time) {
          // 禁止选择未来的时间
          return time.getTime() > Date.now();
        }
      },
      loadUserTimer: null
    };
  },
  watch: {
    open_add: {
      immediate: true,
      handler(newVal) {
        this.dialogVisible = newVal;
        if (newVal) {
          this.$nextTick(() => {
            this.initForm();
          });
        }
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit('close');
      }
    }
  },
  methods: {
    // 获取状态标签类型
    getStatusTag(status) {
      const statusMap = {
        0: 'warning',     // 识别中
        1: 'primary',     // 待归档
        2: 'success'      // 已归档
      };
      return statusMap[status] || 'info';
    },
    
    // 获取状态文本
    getStatusText(status) {
      const statusTextMap = {
        0: '识别中',
        1: '待归档',
        2: '已归档'
      };
      return statusTextMap[status] || '未知';
    },
    
    // 状态变更处理
    handleStatusChange(value) {
      // 清除相关字段的验证
      if (this.$refs.form) {
        if (value !== 1) {
          this.form.identify_time = '';
        }
        if (value !== 2) {
          this.form.signature = '';
        }
        this.$refs.form.clearValidate(['identify_time', 'signature']);
      }
    },
    
    // 初始化表单
    initForm() {
      this.form.status = this.current_status;
      this.form.identify_time = '';
      this.form.signature = '';
      
      // 清除验证
      if (this.$refs.form) {
        this.$refs.form.clearValidate();
      }
      
      // 获取风险管控详情（如果有需要预填的数据）
      if (this.risk_control_id) {
        this.fetchRiskDetail();
      }
    },
    
    // 获取风险管控详情
    fetchRiskDetail() {
      Risk_controlApi.detail({ risk_control_id: this.risk_control_id }, true)
        .then(res => {
          if (res.code === 1 && res.data) {
            const data = res.data;
            // 如果有已存在的识别时间或签字，预填
            if (data.identify_time) {
              this.form.identify_time = data.identify_time;
            }
            if (data.signature) {
              this.form.signature = data.signature;
            }
          }
        })
        .catch(error => {
          console.error('获取详情失败:', error);
        });
    },
    
    // 弹窗打开时
    handleOpen() {
      this.initForm();
    },
    
    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false;
      this.loading = false;
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (valid) {
          // 检查是否真的改变了状态
          if (this.form.status === this.current_status) {
            this.$message.warning('状态未发生变化');
            return;
          }
          
          // 确认对话框
          this.$confirm(
            `确认将状态从"${this.getStatusText(this.current_status)}"变更为"${this.getStatusText(this.form.status)}"吗？`,
            '状态变更确认',
            {
              confirmButtonText: '确认变更',
              cancelButtonText: '取消',
              type: 'warning',
              beforeClose: (action, instance, done) => {
                if (action === 'confirm') {
                  instance.confirmButtonLoading = true;
                  this.updateStatus().finally(() => {
                    instance.confirmButtonLoading = false;
                    done();
                  });
                } else {
                  done();
                }
              }
            }
          ).catch(() => {});
        }
      });
    },
    
    // 更新状态
    async updateStatus() {
      this.loading = true;
      
      try {
        const params = {
          risk_control_id: this.risk_control_id,
          status: this.form.status
        };
        
        // 添加额外的字段
        if (this.form.status === 1 && this.form.identify_time) {
          params.identify_time = this.form.identify_time;
        }
        if (this.form.status === 2 && this.form.signature) {
          params.signature = this.form.signature;
        }
        
        const res = await Risk_controlApi.updateStatus(params, true);
        
        if (res.code === 1) {
          this.$message.success('状态更新成功');
          this.dialogVisible = false;
          this.$emit('success', {
            risk_control_id: this.risk_control_id,
            status: this.form.status
          });
        } else {
          this.$message.error(res.msg || '状态更新失败');
        }
      } catch (error) {
        console.error('状态更新失败:', error);
        this.$message.error('状态更新失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    
    // 处理部门变更（如果还需要）
    async handleDeptChange(type, deptId) {
      if (this.loadUserTimer) {
        clearTimeout(this.loadUserTimer);
      }
      this.loadUserTimer = setTimeout(async () => {
        try {
          const response = await this.$api.Afunc.call({
            model: 'ma_employee',
            action: 'getList',
            deptId: deptId
          });
          if (response.code === 1 && response.data.list) {
            // 处理获取到的员工列表
          }
        } catch (error) {
          console.error('获取员工列表失败:', error);
        }
      }, 100);
    }
  },
  beforeDestroy() {
    // 清理定时器
    if (this.loadUserTimer) {
      clearTimeout(this.loadUserTimer);
      this.loadUserTimer = null;
    }
  }
};
</script>

<style scoped>
.status-change-info {
  padding: 8px 12px;
  background-color: #f4f4f5;
  border-radius: 4px;
  color: #909399;
}

.status-change-info .el-tag {
  margin: 0 4px;
}

.form-tips {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.current-status .el-tag {
  font-size: 14px;
  padding: 8px 12px;
}

.dialog-footer {
  text-align: right;
}
</style>


// WEBPACK FOOTER //
// src/views/plus/risk_control/index/part/status.vue